import PropTypes from 'prop-types';
import { connect } from 'react-redux';
import React, { Component } from 'react';
import { bindActionCreators } from 'redux';
import { UserType } from '@upstart/harmony/lib/constants';
import DealerNav from '../../components/DealerNav';
import EmployeeNav from '../../components/EmployeeNav';
import SidebarContainer from '../SidebarContainer';
import usersActions from '../../redux/user/actions';
import employeesActions from '../../redux/employee/actions';
import dealersActions from '../../redux/dealers/actions';
import ContentPage from '../ContentPage';
import ErrorMessage from '../../components/ErrorMessage';
import { getRoles } from '../../redux/permissions/selectors';
import permissionsActions from '../../redux/permissions/actions';
import './User.scss';

import {
  UserEditor,
  NewUser,
  UserPasswordEditor,
  AccessEditor,
} from '../../components/UserEditor';

class User extends Component {
  constructor(props) {
    super(props);
    this.state = {
      user: props.user,
      employee: props.employee,
      dealers: props.dealers,
    };
  }

  componentDidMount() {
    const { isEmployeeRoute } = this.props;
    const { userId, dealerId } = this.props.match.params;
    if (userId) {
      this.props.actions.userRequest(userId);
      if (isEmployeeRoute) {
        this.props.actions.dealersRequest({ addExtraInformation: false });
      }
    } else {
      this.props.actions.resetUser(dealerId);
      this.props.actions.resetEmployee(dealerId);
    }
    this.props.actions.userRolesRequest();
  }

  componentDidUpdate(prevProps) {
    if (prevProps.user !== this.props.user) {
      this.setState({ user: this.props.user });
    }
    if (
      prevProps.employee?.employee?.id !== this.props.employee?.employee?.id
    ) {
      this.setState({ employee: this.props.employee });
    }
    if (
      prevProps.dealers?.dealers !== this.props.dealers?.dealers ||
      prevProps.dealers?.fetching !== this.props.dealers?.fetching
    ) {
      this.setState({ dealers: this.props.dealers });
    }
  }

  onCreate = params => {
    const { isEmployeeRoute } = this.props;
    if (isEmployeeRoute) {
      this.props.actions.createEmployeeRequest(params);
    } else {
      this.props.actions.createUserRequest(params);
    }
  };

  getRedirectUrlAfterSaving = () => {
    const { isEmployeeRoute, user } = this.props;
    return isEmployeeRoute ? '/employees' : `/${user.dealer_id}/users`;
  };

  onSave = user => {
    // Dealers contains entire dealer objects so is a huge payload and is ignored by backend
    const { Dealers, ...userWithoutDealers } = user;
    this.props.actions.updateUserRequest({
      user: userWithoutDealers,
      redirectTo: this.getRedirectUrlAfterSaving(),
    });
  };

  onUpdateDealers = updatedDealerMembership => {
    const { actions } = this.props;
    const { user } = this.state.user;
    actions.updateUserDealersRequest({
      user,
      dealers: updatedDealerMembership,
      redirectTo: this.getRedirectUrlAfterSaving(),
    });
  };

  onChangePassword = user => {
    if (user.type === UserType.EMPLOYEE) {
      this.props.actions.updateEmployeePasswordRequest(user);
    } else {
      this.props.actions.updateUserPasswordRequest(user);
    }
  };

  renderEditor = () => {
    const { isEmployeeRoute, roles, changePassword, changeAccess } = this.props;
    const { fetching: userFetching, currentUser, user } = this.state.user;
    const { fetching: employeeFetching } = this.state.employee;
    const { fetching: dealersFetching, dealers, dealer } = this.state.dealers;

    const isFetching =
      dealersFetching || employeeFetching || userFetching || false;

    if (user.id) {
      if (changePassword) {
        return (
          <UserPasswordEditor
            user={user}
            fetching={isFetching}
            onChangePassword={this.onChangePassword}
            roles={roles}
          />
        );
      }
      if (changeAccess) {
        return (
          <AccessEditor
            user={user}
            fetching={isFetching}
            onSave={this.onUpdateDealers}
            dealers={dealers}
          />
        );
      }
      return (
        currentUser && (
          <UserEditor
            user={user}
            dealer={dealer}
            currentUser={currentUser}
            isEmployeeRoute={isEmployeeRoute}
            fetching={isFetching}
            onSave={this.onSave}
            accessibleRoles={currentUser.accessibleRoles}
          />
        )
      );
    }

    return (
      currentUser && (
        <NewUser
          user={user}
          dealer={dealer}
          isEmployeeRoute={isEmployeeRoute}
          fetching={isFetching}
          onCreate={this.onCreate}
          accessibleRoles={currentUser?.accessibleRoles}
        />
      )
    );
  };

  render() {
    const { dealers, user, employee, isEmployeeRoute } = this.props;
    const dealer = dealers.dealer;
    const dealerProp = isEmployeeRoute ? {} : dealer;
    const error = this.state.user.error || employee.error;

    return (
      <SidebarContainer title="User" logo={dealer.logo}>
        <div className="users-table">
          {dealerProp.id && (
            <DealerNav dealer={dealerProp} user={this.state.user.currentUser} />
          )}
          {isEmployeeRoute && <EmployeeNav user={user.user} />}
          {error && <ErrorMessage error={error} />}
          {this.renderEditor()}
        </div>
      </SidebarContainer>
    );
  }
}

User.propTypes = {
  match: PropTypes.object,
  user: PropTypes.object,
  employee: PropTypes.object,
  isEmployeeRoute: PropTypes.bool,
  dealers: PropTypes.object,
  actions: PropTypes.object,
  roles: PropTypes.array,
  changePassword: PropTypes.bool,
  changeAccess: PropTypes.bool,
};

const mapStateToProps = ({ user, employee, dealers, permissions }) => ({
  user,
  employee,
  dealers,
  roles: getRoles(permissions),
});

const mapDispatchToProps = dispatch => ({
  actions: bindActionCreators(
    {
      ...usersActions,
      ...employeesActions,
      ...permissionsActions,
      ...dealersActions,
    },
    dispatch,
  ),
});

export default connect(mapStateToProps, mapDispatchToProps)(ContentPage(User));
